import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Stack,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from '@mantine/core';
import {
  IconMapPin,
  IconMessageCircle,
  IconRefresh,
  IconSearch,
  IconX,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { resolveMediaUrl } from '@domain/media/resolve';
import { fetchMapArticles } from '@domain/plaza/api';
import type { Article } from '@domain/plaza/types';
import { UserAvatar } from '@shared/ui/UserAvatar';
import { formatListTime, getErrorMessage } from '@shared/utils/ui';
import { LocsMapCanvas } from '../components/LocsMapCanvas';
import type { LocsMapMarker } from '../types/mapProvider';

type LocsMapStageProps = {
  apiBase: string;
  cdnBase: string;
  active: boolean;
  viewSwitcher: ReactNode;
  onStartConversation: (authorUserId: number) => void;
  onViewProfile: (authorUserId: number) => void;
  refreshKey?: number;
};

export function LocsMapStage({
  apiBase,
  cdnBase,
  active,
  viewSwitcher,
  onStartConversation,
  onViewProfile,
  refreshKey,
}: LocsMapStageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
  const [mapArticles, setMapArticles] = useState<Article[]>([]);
  const [loadState, setLoadState] = useState<'loading' | 'idle' | 'error'>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  // Track whether we have ever successfully loaded data.
  // Only show the full-screen loading overlay on the very first load;
  // subsequent refreshKey-triggered reloads run silently in the background.
  const hasLoadedOnce = useRef(false);

  const loadMapArticles = useCallback(async () => {
    // Show loading overlay only before the first successful load.
    if (!hasLoadedOnce.current) {
      setLoadState('loading');
    }
    setLoadError(null);
    try {
      setMapArticles(await fetchMapArticles(apiBase));
      hasLoadedOnce.current = true;
      setLoadState('idle');
    } catch (error) {
      setLoadError(getErrorMessage(error));
      setLoadState('error');
    }
  }, [apiBase]);

  useEffect(() => {
    void loadMapArticles();
  // refreshKey change triggers a reload when switching back to the map tab
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMapArticles, refreshKey]);

  const visibleArticles = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLocaleLowerCase();
    return mapArticles.filter((article) => {
      if (!article.location) return false;
      const authorName = article.author?.displayName ?? '';
      const matchesQuery = !normalizedQuery || [
        authorName,
        article.title,
        article.body,
        article.location.label,
        ...article.tags,
      ].some((value) => value.toLocaleLowerCase().includes(normalizedQuery));
      return matchesQuery;
    });
  }, [mapArticles, searchQuery]);

  const selectedArticle = visibleArticles.find((article) => article.id === selectedArticleId) ?? null;
  const markers = useMemo<LocsMapMarker[]>(() => visibleArticles.flatMap((article) => {
    if (!article.location) return [];
    const authorName = article.author?.displayName || `用户 #${article.authorUserId}`;
    return [{
      id: article.id,
      location: { lat: article.location.latitude, lng: article.location.longitude },
      label: `${authorName}，${article.location.label}，${article.title}`,
      glyph: authorName.slice(0, 1).toUpperCase(),
      decorated: false,
      selected: article.id === selectedArticleId,
    }];
  }), [selectedArticleId, visibleArticles]);

  const clearSelection = () => setSelectedArticleId(null);

  return (
    <Box className="locs-map-stage" data-testid="hubs-map-stage">
      <Box className="locs-map-stage__filters hubs-stage__toolbar">
        {viewSwitcher}
        <TextInput
          className="locs-map-stage__search"
          value={searchQuery}
          onChange={(event) => {
            setSearchQuery(event.currentTarget.value);
            clearSelection();
          }}
          leftSection={<IconSearch size={17} stroke={2} />}
          placeholder="按作者、区域或动态搜索"
          aria-label="搜索地图动态"
          styles={{
            input: { border: 'none', outline: 'none', boxShadow: 'none' },
          }}
        />
      </Box>

      <Box className="locs-map-stage__workspace">
        <Box className="locs-map-stage__map" aria-label="社区动态地图">
          <LocsMapCanvas active={active} markers={markers} onSelectMarker={setSelectedArticleId} />

          {loadState === 'loading' && (
            <Center className="locs-map-stage__empty">
              <Stack gap="xs" align="center">
                <Loader size="sm" color="terracotta" />
                <Text size="sm" fw={700}>正在加载附近动态</Text>
              </Stack>
            </Center>
          )}

          {loadState === 'error' && (
            <Center className="locs-map-stage__empty">
              <Stack gap="sm" align="center" ta="center" maw={360}>
                <Text fw={850}>地图动态加载失败</Text>
                <Text size="sm" c="dimmed">{loadError || '请稍后重试'}</Text>
                <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={() => void loadMapArticles()}>
                  重新加载
                </Button>
              </Stack>
            </Center>
          )}

          {selectedArticle?.location ? (
            <Box
              className="locs-map-stage__selection-card"
              role="dialog"
              aria-live="polite"
              aria-label={`${selectedArticle.author?.displayName || '社区成员'}的地图动态`}
            >
              <ActionIcon
                className="locs-map-stage__selection-close"
                variant="subtle"
                radius="xl"
                aria-label="关闭地图动态"
                onClick={clearSelection}
              >
                <IconX size={17} />
              </ActionIcon>
              <Stack gap="md">
                <Group gap="sm" wrap="nowrap" align="flex-start">
                  <UnstyledButton
                    className="locs-map-stage__selection-avatar-trigger"
                    aria-label="查看用户个人主页"
                    onClick={() => onViewProfile(selectedArticle.authorUserId)}
                  >
                    <UserAvatar
                      className="locs-map-stage__selection-avatar"
                      id={selectedArticle.authorUserId}
                      name={selectedArticle.author?.displayName || `用户 #${selectedArticle.authorUserId}`}
                      avatarUrl={resolveMediaUrl(selectedArticle.author?.avatar, cdnBase)}
                      size={46}
                    />
                  </UnstyledButton>
                  <Box style={{ minWidth: 0 }}>
                    <Title order={2} className="locs-map-stage__selection-name">
                      {selectedArticle.author?.displayName || `用户 #${selectedArticle.authorUserId}`}
                    </Title>
                    <Text size="xs" c="var(--locs-gold)">{formatListTime(selectedArticle.created)}</Text>
                  </Box>
                </Group>
                <Group gap={6} className="locs-map-stage__selection-location" wrap="nowrap">
                  <IconMapPin size={15} />
                  <Text size="sm">{selectedArticle.location.label} · 大致位置</Text>
                </Group>
                <Box>
                  <Text fw={850}>{selectedArticle.title}</Text>
                  {selectedArticle.body && (
                    <Text size="sm" className="locs-map-stage__selection-note" lineClamp={3} mt={4}>
                      {selectedArticle.body}
                    </Text>
                  )}
                </Box>
                {selectedArticle.tags.length > 0 && (
                  <Group gap={6}>
                    {selectedArticle.tags.map((tag) => (
                      <Badge key={tag} variant="outline" color="terracotta" radius="xl">#{tag}</Badge>
                    ))}
                  </Group>
                )}
                <Group grow gap="sm">
                  <Button
                    variant="light"
                    color="terracotta"
                    leftSection={<IconMessageCircle size={17} />}
                    onClick={() => onStartConversation(selectedArticle.authorUserId)}
                  >
                    聊天
                  </Button>
                </Group>
              </Stack>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
